import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { Menu, Transition } from '@headlessui/react';
import { UserCircleIcon, Bars3Icon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useReminderCount } from '../hooks/useReminderCount';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const getImageUrl = (url) => {
  if (!url) return null;
  // Remove any emulator URLs and return the clean storage URL
  return url.replace('http://localhost:9198/v0/b/', 'https://firebasestorage.googleapis.com/v0/b/');
};

const Header = ({ className = '' }) => {
  const { currentUser, logout } = useAuth();
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const reminderCount = useReminderCount();
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsAdminOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser?.uid) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [currentUser]);

  const handleNavigationClick = () => {
    setIsMobileMenuOpen(false);
    setIsAdminOpen(false);
  };

  const adminLinks = [
    { name: 'Users', path: '/users' },
    { name: 'Groups', path: '/groups' },
    { name: 'Organizations', path: '/organizations' },
    ...(userData?.isSuperAdmin ? [{ name: 'App Settings', path: '/app-admin' }] : [])
  ];

  const mainNavLinks = [
    { name: 'My Disciples', path: '/dashboard' },
    { name: 'Community', path: '/my-community' },
    { 
      name: 'Reminders', 
      path: '/reminders',
      badge: reminderCount > 0 ? reminderCount : null
    }
  ];

  const getInitials = (userData) => {
    if (!userData) return '';
    const firstName = userData.firstName || '';
    const lastName = userData.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const AvatarDisplay = () => {
    if (userData?.photoURL) {
      return (
        <img
          src={getImageUrl(userData.photoURL)}
          alt="Profile"
          className="h-8 w-8 rounded-full object-cover"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = '';
            e.target.className = 'hidden';
          }}
        />
      );
    }
    
    if (userData?.firstName || userData?.lastName) {
      return (
        <div className="h-8 w-8 rounded-full bg-gray-600 bg-opacity-50 flex items-center justify-center text-white text-sm font-medium">
          {getInitials(userData)}
        </div>
      );
    }

    return <UserCircleIcon className="h-8 w-8 text-gray-300" />;
  };

  // Default brand config
  const defaultBrand = {
    name: 'Together On Mission',
    logo: '/images/TOM-logo-512.png'
  };

  const userNavigation = [
    { 
      name: 'Your Profile', 
      href: currentUser?.appID ? `/user/${currentUser.appID}` : '/profile',  // Use appID instead of uid
      isLink: true
    },
    { name: 'Marketplace', href: '/marketplace', isLink: true },
    { name: 'Training Modules', href: '/training-modules', isLink: true },
    { name: 'Sign out', href: '#', onClick: logout, isLink: false },
  ];

  return (
    <header className={`bg-gray-800 sticky top-0 z-50 ${className}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center">
            <Link to="/my-community" className="flex items-center">
              <img
                src={defaultBrand.logo}
                alt="Organization Logo"
                className="w-12 h-12 object-contain mr-3"
              />
              <div className="flex items-center">
                <div className="flex flex-col">
                  <div className="flex items-center">
                    <span className="text-white font-bold text-lg leading-tight">
                      {defaultBrand.name}
                    </span>
                  </div>
                  <span className="text-gray-400 text-xs">
                    Go Make Disciples
                  </span>
                </div>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4">
            {currentUser ? (
              <>
                {mainNavLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className="relative text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    {link.name}
                    {link.badge && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {link.badge}
                      </span>
                    )}
                  </Link>
                ))}
                {/* Share Story Button */}
                <Link
                  to="/share-story"
                  className="bg-secondary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-opacity-90 transition-colors"
                >
                  Share a Story
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="text-gray-300 hover:text-white px-3 py-2">
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="bg-indigo-600 text-white px-3 py-2 rounded-md hover:bg-indigo-500"
                >
                  Sign Up
                </Link>
              </>
            )}
          </nav>

          {/* Mobile menu button */}
          <div className="flex md:hidden">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-400 hover:text-white"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex items-center">
            <Menu as="div" className="relative ml-3">
              <Menu.Button className="flex rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                <AvatarDisplay />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {userNavigation.map((item) => (
                    <Menu.Item key={item.name}>
                      {({ active }) => (
                        item.isLink ? (
                          <Link
                            to={item.href}
                            className={classNames(
                              active ? 'bg-gray-100' : '',
                              'block px-4 py-2 text-sm text-gray-700'
                            )}
                          >
                            {item.name}
                          </Link>
                        ) : (
                          <a
                            href={item.href}
                            onClick={item.onClick}
                            className={classNames(
                              active ? 'bg-gray-100' : '',
                              'block px-4 py-2 text-sm text-gray-700 w-full text-left'
                            )}
                          >
                            {item.name}
                          </a>
                        )
                      )}
                    </Menu.Item>
                  ))}
                  {/* Admin Section */}
                  {userData?.isSuperAdmin && (
                    <>
                      <div className="border-t border-gray-200 my-1" />
                      <div className="px-4 py-2 text-xs font-semibold text-gray-400">Admin</div>
                      {adminLinks.map((item) => (
                        <Menu.Item key={item.name}>
                          {({ active }) => (
                            <Link
                              to={item.path}
                              className={classNames(
                                active ? 'bg-gray-100' : '',
                                'block px-4 py-2 text-sm text-gray-700'
                              )}
                            >
                              {item.name}
                            </Link>
                          )}
                        </Menu.Item>
                      ))}
                    </>
                  )}
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>

        {/* Mobile menu panel */}
        <Transition
          show={isMobileMenuOpen}
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <div className="md:hidden">
            <div className="space-y-1 px-2 pb-3 pt-2">
              {currentUser ? (
                <>
                  {/* Main nav links */}
                  {mainNavLinks.map((link) => (
                    <Link
                      key={link.name}
                      to={link.path}
                      className="relative block rounded-md px-3 py-2 text-base font-medium text-gray-300 hover:bg-gray-700 hover:text-white"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.name}
                      {link.badge && (
                        <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                          {link.badge}
                        </span>
                      )}
                    </Link>
                  ))}
                   {/* Share Story Link for Mobile */}
                   <Link
                    to="/share-story"
                    className="block rounded-md bg-secondary px-3 py-2 text-base font-medium text-white hover:bg-opacity-90"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Share a Story
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block text-gray-300 hover:text-white px-3 py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="block bg-indigo-600 text-white px-3 py-2 rounded-md hover:bg-indigo-500"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        </Transition>
      </div>
    </header>
  );
};

export default Header;